import { decodeJwtPayload } from './jwtUtils.js';

/** Backend API JWT in localStorage — cleared on sign-out / auth failure (not a full storage wipe). */
export const TOKEN_KEY = 'api_access_token';

/** Centralised localStorage key names used by the auth package and consumers. */
export const AUTH_STORAGE_KEYS = {
  TOKEN: TOKEN_KEY,
  USER_UUID: 'user_uuid',
  WORKSPACE_UUID: 'workspace_uuid',
};
const TOKEN_URL = (import.meta.env.VITE_API_BASE_URL || '') + '/v1/token';

/**
 * Mirror the JWT's identity claims into the `user_uuid` / `workspace_uuid`
 * localStorage keys.
 *
 * Those two keys are read in 11 places across 6 apps — every `apiClient`'s
 * `refreshToken`, `ShellLayout`, the Agents Knowledge tab, work-queues' task
 * owner — but until now the ONLY code that ever wrote them lived inside
 * `useAuthState.fetchApiToken`, i.e. the Auth0 exchange. A cross-subdomain
 * handoff (`tokenHandoff.js`) carries the JWT to the new origin and nothing
 * else, and `localStorage` does not cross origins, so on a handoff-landed
 * subdomain both keys stayed empty forever while a perfectly good token sat
 * next to them. That is CT-5304 (Knowledge upload: "No workspace / tenant"),
 * and it is the same hole that CT-5288 patched for `userId` and PR #466
 * patched for token renewal — each time for one consumer, never at the source.
 *
 * **The workspace id is the `tenant_uuid` claim, not `workspace_uuid`.** The
 * rest of the app treats tenant as workspace, and the `workspace_uuid` claim
 * is not always present — see the reasoning in `renewFromCurrentClaims` and
 * `tokenService.test.js`.
 *
 * Writes only what it can resolve: a token missing a claim leaves that key
 * untouched rather than blanking a value that is still correct.
 *
 * @param {string} token
 * @returns {{ userUuid: string|null, workspaceUuid: string|null }} what was written
 */
export const syncIdentityKeysFromToken = (token) => {
  const claims = decodeJwtPayload(token) || {};
  const userUuid = claims.user_uuid || null;
  const workspaceUuid = claims.tenant_uuid || null;

  if (userUuid) localStorage.setItem(AUTH_STORAGE_KEYS.USER_UUID, userUuid);
  if (workspaceUuid) localStorage.setItem(AUTH_STORAGE_KEYS.WORKSPACE_UUID, workspaceUuid);

  return { userUuid, workspaceUuid };
};

let isRefreshing = false;
let requestQueue = [];
let tokenChangeListeners = [];

const processQueue = (error, token = null) => {
  requestQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  requestQueue = [];
};

let _auth0TokenGetter = null;

export const tokenService = {
  getToken: () => localStorage.getItem(TOKEN_KEY),

  setToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    tokenChangeListeners.forEach((cb) => {
      try {
        cb(token);
      } catch (e) {
        console.error('[tokenService] onTokenChange listener error:', e);
      }
    });
  },

  onTokenChange: (callback) => {
    tokenChangeListeners.push(callback);
    return () => {
      tokenChangeListeners = tokenChangeListeners.filter((cb) => cb !== callback);
    };
  },

  getTokenExpiresInMs: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return null;
    return payload.exp * 1000 - Date.now();
  },

  setAuth0TokenGetter: (getter) => {
    _auth0TokenGetter = getter;
  },

  /**
   * Fetch a new backend token using user_uuid and tenant_uuid (dev/fallback mode).
   */
  fetchNewToken: async (userUuid, tenantUuid, workspaceUuid) => {
    const body = { user_uuid: userUuid, tenant_uuid: tenantUuid };
    // The backend only re-issues a workspace-scoped JWT when workspace_uuid is
    // included; without it the call returns 200 with no token.
    if (workspaceUuid) body.workspace_uuid = workspaceUuid;
    const response = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`Token request failed: ${response.status}`);
    }
    const data = await response.json();
    const newToken = data.body?.token ?? data.token;
    if (!newToken) {
      throw new Error('Token response missing token');
    }
    tokenService.setToken(newToken);
    return newToken;
  },

  /**
   * Exchange Auth0 access token for backend API token.
   *
   * POST /v1/token with Authorization: Bearer <auth0Token> and one of:
   *
   *   • First login  (user_uuid not yet in localStorage):
   *       body = { sub }
   *     Backend resolves the user from the Auth0 sub and assigns the default workspace.
   *
   *   • Reload / session continuation (user_uuid + workspace_uuid already in localStorage):
   *       body = { user_uuid, workspace_uuid }
   *     Backend returns a JWT scoped to the stored workspace. This preserves
   *     an explicit workspace switch across page reloads — sending only { sub }
   *     would return the default workspace JWT and overwrite the selection.
   *
   * The backend rejects { sub, workspace_uuid } with CT0006 ("user_uuid and
   * workspace_uuid must both be provided together"), so workspace_uuid must
   * never be mixed with sub.
   */
  fetchTokenWithAuth0: async (auth0AccessToken) => {
    const payload = decodeJwtPayload(auth0AccessToken);
    if (!payload) {
      throw new Error('Invalid Auth0 token: could not decode JWT');
    }
    const sub = payload.sub;
    if (typeof sub !== 'string' || !sub.trim()) {
      throw new Error('Auth0 token missing or invalid sub');
    }

    const storedUserUuid = localStorage.getItem(AUTH_STORAGE_KEYS.USER_UUID);
    const storedWorkspaceUuid = localStorage.getItem(AUTH_STORAGE_KEYS.WORKSPACE_UUID);

    // When both UUIDs are persisted the user already has a session (or just
    // switched workspace). Send them explicitly so the backend returns the
    // correct workspace JWT without falling back to the user's default.
    const requestBody = (storedUserUuid && storedWorkspaceUuid)
      ? { user_uuid: storedUserUuid, workspace_uuid: storedWorkspaceUuid }
      : { sub };

    const response = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth0AccessToken}`,
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }
    const data = await response.json();
    const newToken = data.body?.token ?? data.token;
    if (!newToken) {
      throw new Error('Token response missing token');
    }
    tokenService.setToken(newToken);
    return newToken;
  },

  /**
   * Refresh the backend API token.
   * Uses Auth0 token getter when available, otherwise falls back to user/tenant UUIDs.
   * Queues concurrent callers so only one refresh request is in-flight at a time.
   */
  refreshToken: async (userUuid, tenantUuid) => {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      });
    }

    isRefreshing = true;
    try {
      let token;

      if (_auth0TokenGetter) {
        const auth0AccessToken = await _auth0TokenGetter();
        token = await tokenService.fetchTokenWithAuth0(auth0AccessToken);
      } else {
        // Cross-subdomain handoff origin — no Auth0 session here, but the
        // current backend JWT carries the identity, so renew from its own
        // claims. The workspace id is the `tenant_uuid` claim: the rest of the
        // app treats it as the workspace (useAuthState stores tenant_uuid into
        // localStorage.workspace_uuid), and it is always present — whereas the
        // workspace_uuid claim can be absent, which would drop the field and
        // make /v1/token return 200 with no token. /v1/token needs
        // workspace_uuid present or a long-lived subdomain tab still expires.
        const claims = decodeJwtPayload(localStorage.getItem(TOKEN_KEY)) || {};
        const workspaceId = tenantUuid || claims.tenant_uuid;
        token = await tokenService.fetchNewToken(
          userUuid || claims.user_uuid,
          workspaceId,
          workspaceId,
        );
      }

      processQueue(null, token);
      return token;
    } catch (err) {
      processQueue(err, null);
      throw err;
    } finally {
      isRefreshing = false;
    }
  },
};
